import { MarginMultiplierHint } from "./MarginMultiplierHint";

/**
 * The three answers a brand-new category cannot exist without: its name, the
 * VAT rate that applies to every item in it (G-30), and the margin the pricing
 * guidance aims at (G-45).
 *
 * Extracted from `CategoryPicker` in G-90 so the create-item wizard, whose
 * first screen is a grid of tiles rather than a `<select>`, can ask for them
 * without a second copy of these inputs and their bounds. Two sets of fields
 * claiming the same rule is how they come apart.
 */
export interface NewCategoryFieldsProps {
  /** Name for the to-be-created category. */
  label: string;
  onLabelChange: (next: string) => void;
  /** G-30: VAT percent, as a draft string. */
  vatPercent: string;
  onVatPercentChange: (next: string) => void;
  /** G-45: target margin, as a draft string. */
  targetMarginPercent: string;
  onTargetMarginPercentChange: (next: string) => void;
  /** Id for the explanatory paragraph, so a caller can `aria-describedby` it. */
  hintId?: string;
}

export function NewCategoryFields({
  label,
  onLabelChange,
  vatPercent,
  onVatPercentChange,
  targetMarginPercent,
  onTargetMarginPercentChange,
  hintId = "newCategoryHint",
}: NewCategoryFieldsProps) {
  return (
    <>
      <p id={hintId} className="field-hint" style={{ marginTop: "var(--space-2)" }}>
        New category: name · VAT % (flowers 10, most other things 21) · target margin % (a share
        of the price before IVA) · created when you save the item
      </p>
      <label htmlFor="newCategoryName" className="visually-hidden">
        New category name
      </label>
      <input
        id="newCategoryName"
        type="text"
        className="other-category-input"
        placeholder="New category name *"
        maxLength={120}
        value={label}
        onChange={(e) => onLabelChange(e.target.value)}
        required
      />
      <label htmlFor="newCategoryVat" className="visually-hidden">
        VAT percent for the new category
      </label>
      <input
        id="newCategoryVat"
        type="number"
        className="other-category-input"
        placeholder="VAT % *"
        inputMode="decimal"
        min={0}
        max={100}
        step={0.01}
        value={vatPercent}
        onChange={(e) => onVatPercentChange(e.target.value)}
        required
      />
      <label htmlFor="newCategoryTargetMargin" className="visually-hidden">
        Target margin percent for the new category
      </label>
      <input
        id="newCategoryTargetMargin"
        type="number"
        className="other-category-input"
        placeholder="Target margin % *"
        inputMode="decimal"
        min={0}
        /* Exclusive of 100: the price formula divides by (1 - m/100). */
        max={99.99}
        step={0.01}
        value={targetMarginPercent}
        onChange={(e) => onTargetMarginPercentChange(e.target.value)}
        required
      />
      <MarginMultiplierHint targetMarginPercent={targetMarginPercent} vatRatePercent={vatPercent} />
    </>
  );
}
